<script setup lang="ts">
import type { AccordionItem } from '@nuxt/ui';
import type { BlockFaq, BlockFaqQuestion } from '~/types';
import { markdownToHtml } from '~/utils/markdown';

const props = defineProps<{
  data: BlockFaq;
}>();

const offset = ref(0);
const limit = ref(10);

const faqs = computed(() => {
  // We don't want to overwhelm the user with too many FAQs at once so let's only show 5 until they want more
  return props.data?.faqs?.slice(offset.value, limit.value).map((item: BlockFaqQuestion, index: number) => {
    return {
      label: item?.title,
      content: item?.answer,
      value: String(index),
    };
  });
});

function loadMore() {
  limit.value += 5;
}

const accordionUi = {
  root: 'space-y-2',
  item: 'border-b-0',
  header: 'flex',
  trigger:
    'group rounded-card relative w-full px-6 py-4 flex items-center justify-between gap-4 text-left text-gray-400 transition duration-150 bg-gray-100 dark:bg-gray-800 data-[state=open]:bg-primary/20 cursor-pointer focus:outline-none focus:text-gray-900 dark:focus:text-primary',
  label: 'text-sm font-medium text-gray-900 dark:text-white font-display md:text-base',
  trailingIcon: 'hidden',
  content:
    'data-[state=open]:animate-[accordion-down_200ms_ease-out] data-[state=closed]:animate-[accordion-up_200ms_ease-out] overflow-hidden focus:outline-none',
  body: 'relative px-6 pt-2 pb-6',
};
</script>

<template>
  <BlockContainer>
    <div
      :class="[
        {
          'mx-auto max-w-3xl': data.alignment === 'center',
        },
      ]"
    >
      <TypographyTitle v-if="data.title">{{ data.title }}</TypographyTitle>
      <TypographyHeadline v-if="data.headline" :content="data.headline" size="lg" />
      <div class="pt-6 mt-6">
        <UAccordion v-if="faqs" v-auto-animate :items="faqs as Partial<AccordionItem>[]" :ui="accordionUi">
          <template #trailing="{ open }">
            <span class="flex items-center">
              <UIcon
                :name="open ? 'material-symbols:remove-rounded' : 'material-symbols:add-rounded'"
                class="w-8 h-8 rounded-full fill-current text-primary cursor-pointer"
              />
            </span>
          </template>
          <template #body="{ item }">
            <div class="prose text-left dark:prose-invert">
              <div v-html="markdownToHtml((item as any)?.content)" />
            </div>
          </template>
        </UAccordion>
        <div class="mt-2 text-center">
          <UButton
            v-if="faqs && props.data.faqs && faqs.length < props.data.faqs.length"
            variant="solid"
            size="xl"
            class="text-white dark:text-white"
            @click="loadMore"
          >
            Load More
          </UButton>
        </div>
      </div>
    </div>
  </BlockContainer>
</template>
